import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "react-oauth2-code-pkce";
import AIIconFullCBlack from "../../assets/AI_Icon_fullC_black.svg";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
const AiAssistantDispatchEvent = {
    INVALID_CONFIGURATION: "ai-assistant-invalid-configuration",
    LOADED: "ai-assistant-loaded",
    NOTIFICATION_EVENT: "ai-assistant-notification-event",
    REDIRECTION_LOGIN: "ai-assistant-redirection-login",
    TOKEN_EXPIRED: "ai-assistant-token-expired",
    TOKEN_INVALID: "ai-assistant-token-invalid",
    TOKEN_PENDING_EXPIRATION: "ai-assistant-token-pending-expiration"
};
const AiAssistant = () => {
    const { logIn, token } = useContext(AuthContext);
    const { primaryPartyType, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        userPartyNumber: state.userPartyNumber
    }));
    const { projectDetails } = useProjectDetailsStore((state) => ({
        projectDetails: state.projectDetails
    }));
    const resolvedAccessToken = useMemo(() => {
        if (token) {
            return token;
        }
        return localStorage.getItem("access_token")?.replace(/"/g, "") || "";
    }, [token]);
    /**
     * Constructs user preferences object for AI Assistant
     * Includes language and other user/default preferences in base64 encoded JSON format
     */
    const aiAssistantUserPreferences = useMemo(() => {
        const userProvided = {
            language: "en-US"
        };
        const defaults = {
            assetLabel: "name",
            assetLocationLabel: "address",
            currency: "USD",
            dateFormat: "ddmmyyyy",
            language: "en-US",
            numberFormat: "radixComa",
            pressureUnit: "kpa",
            systemOfMeasurement: "metric",
            temperatureUnit: "C",
            timeFormat: "hours12",
            timeZone: "UTC",
            windowsTimeZone: "GMT Standard Time"
        };
        return btoa(JSON.stringify({ defaults, userProvided }));
    }, []);
    /**
     * Constructs application context for AI Assistant
     * Currently includes the application name identifier
     */
    const aiAssistantAppContext = useMemo(() => {
        const isCatUser = primaryPartyType?.toUpperCase() === "CAT";
        const location = projectDetails?.streetAddress || "";
        const projectName = projectDetails?.projectName || "";
        return JSON.stringify({
            appName: "eflow.cat.com",
            conditions: {
                filters: {
                    downSelectPartyNumber: isCatUser ? userPartyNumber || "" : "",
                    location,
                    partyNumber: !isCatUser ? userPartyNumber || "" : "",
                    site_name: projectName,
                    subscribed: true
                }
            }
        });
    }, [primaryPartyType, projectDetails?.projectName, projectDetails?.streetAddress, userPartyNumber]);
    /**
     * AI Assistant environment.
     * Temporarily hardcoded to "preprod" for all environments during initial integration.
     * TODO: Switch to dynamic mapping via getAppEnv() once preprod environment is validated.
     */
    const aiAssistantEnv = "prod";
    /**
     * Registers AI Assistant event listeners for authentication and lifecycle events
     * Handles token expiration, authentication errors, and component lifecycle events
     * New in v1.2.0: LOADED and NOTIFICATION_EVENT handlers
     */
    useEffect(() => {
        const handleTokenExpired = (event) => {
            // Log the token has expired event
            console.log("AI Assistant: Token has Expired*****", event);
            // Relogin when token is expired
            if (logIn) {
                logIn();
            }
        };
        const handleTokenPendingExpiration = (event) => {
            // Log the token pending expiration event. react-oauth2-code-pkce lib should auto refresh the token
            console.log("AI Assistant: Token about to Expire*****", event);
        };
        const handleTokenInvalid = (event) => {
            // Log the invalid token details - Can enhance later
            console.log("AI Assistant: Invalid configuration*****", event.detail);
            // Trigger re-authentication
            if (logIn) {
                logIn();
            }
        };
        const handleRedirectionLogin = (event) => {
            // Log the redirection login event
            console.log("AI Assistant: Redirect to login*****", event);
            if (logIn) {
                logIn();
            }
        };
        // To debug invalid configuration passed to AI Assistant
        const handleInvalidConfiguration = (event) => {
            // Log the invalid configuration details - Can enhance later
            console.log("AI Assistant: Invalid configuration*****", event.detail);
        };
        const handleLoaded = (event) => {
            // Log when AI Assistant is loaded
            console.log("AI Assistant: Loaded*****", event);
        };
        const handleNotification = (event) => {
            // Handle notification events from AI Assistant
            // Log only serializable data to avoid circular reference errors during JSON stringification
            const customEvent = event;
            console.log("AI Assistant: Notification Event*****", {
                detail: customEvent.detail,
                type: customEvent.type
            });
        };
        // Add listeners for AI Assistant events using the enum values
        document.addEventListener(AiAssistantDispatchEvent.TOKEN_EXPIRED, handleTokenExpired);
        document.addEventListener(AiAssistantDispatchEvent.TOKEN_PENDING_EXPIRATION, handleTokenPendingExpiration);
        document.addEventListener(AiAssistantDispatchEvent.TOKEN_INVALID, handleTokenInvalid);
        document.addEventListener(AiAssistantDispatchEvent.REDIRECTION_LOGIN, handleRedirectionLogin);
        document.addEventListener(AiAssistantDispatchEvent.INVALID_CONFIGURATION, handleInvalidConfiguration);
        document.addEventListener(AiAssistantDispatchEvent.LOADED, handleLoaded);
        document.addEventListener(AiAssistantDispatchEvent.NOTIFICATION_EVENT, handleNotification);
        return () => {
            // Cleanup event listeners
            document.removeEventListener(AiAssistantDispatchEvent.TOKEN_EXPIRED, handleTokenExpired);
            document.removeEventListener(AiAssistantDispatchEvent.TOKEN_PENDING_EXPIRATION, handleTokenPendingExpiration);
            document.removeEventListener(AiAssistantDispatchEvent.TOKEN_INVALID, handleTokenInvalid);
            document.removeEventListener(AiAssistantDispatchEvent.REDIRECTION_LOGIN, handleRedirectionLogin);
            document.removeEventListener(AiAssistantDispatchEvent.INVALID_CONFIGURATION, handleInvalidConfiguration);
            document.removeEventListener(AiAssistantDispatchEvent.LOADED, handleLoaded);
            document.removeEventListener(AiAssistantDispatchEvent.NOTIFICATION_EVENT, handleNotification);
        };
    }, [logIn]);
    return (_jsx("div", { "data-test": "ai-insights-content", children: _jsx("div", { className: "flex mb-4 gap-2", "data-test": "ai-insights-content-container", children: _jsx("div", { className: "flex w-full", "data-test": "ai-insights-panel", children: _jsxs("div", { className: "w-full flex flex-col", children: [_jsxs("div", { className: "flex items-center gap-2 mb-8", "data-test": "cat-ai-assistant-header", children: [_jsx("img", { alt: "AI Insights icon", className: "w-10 h-10", src: AIIconFullCBlack }), _jsx("h1", { className: "text-2xl font-bold tracking-tight", children: "AI Insights" })] }), _jsx(Fragment, { children: _jsx("cat-ai-assistant", { "access-token": resolvedAccessToken, "app-context": aiAssistantAppContext, "data-test": "cat-ai-assistant", environment: aiAssistantEnv, "x-cat-user-preferences": aiAssistantUserPreferences }) }, projectDetails?.projectName ?? "no-project")] }) }) }) }));
};
export default AiAssistant;
